import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { analyseMouth } from '../face/mouth.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

/**
 * Closed-mouth check.
 *
 * Independent from the expression check on purpose: it measures the inner-lip
 * gap and the mouth aspect ratio, and only uses `jawOpen` / `mouthClose` as
 * supporting evidence. A hard failure requires both a clear margin above the
 * threshold and a confident measurement — an uncertain mouth never fails.
 */
export const mouthCheck: ValidationCheck = {
  id: 'mouth',
  title: 'Рот закрыт',
  description: 'Измеряет расстояние между губами и соотношение сторон рта.',
  kind: 'content',
  requires: ['faces', 'geometry'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['faces', 'geometry']);
    if (missing) return notChecked(ctx, mouthCheck, missing, ctx.rule.severity);
    const geometry = ctx.geometry;
    if (!geometry) {
      return notChecked(ctx, mouthCheck, 'Геометрия рта недоступна.', ctx.rule.severity);
    }

    const maxLipGap = numberParam(ctx.rule.params, 'maxLipGap', 0.06);
    const maxMouthOpenRatio = numberParam(ctx.rule.params, 'maxMouthOpenRatio', 0.35);
    const uncertainBelow = numberParam(ctx.rule.params, 'uncertainBelowConfidence', 0.6);

    const face = ctx.faces.faces[0];
    const analysis = analyseMouth({
      landmarks: face?.landmarks ?? geometry.landmarks,
      geometry,
      ...(face?.blendshapes ? { blendshapes: face.blendshapes } : {}),
      ...(ctx.pixels ? { pixels: ctx.pixels } : {}),
    });

    const lipGapOpen = analysis.lipGap > maxLipGap;
    const aspectOpen =
      analysis.mouthWidthReliable && analysis.mouthAspectRatio > maxMouthOpenRatio;
    const blendshapeOpen = (analysis.blendshapeOpen ?? 0) > 0.6;
    const open = lipGapOpen || aspectOpen || blendshapeOpen;
    const strongGeometric =
      analysis.lipGap > maxLipGap * 1.6 &&
      (!analysis.mouthWidthReliable || analysis.mouthAspectRatio > maxMouthOpenRatio * 1.1);
    const strongBlendshape = (analysis.blendshapeOpen ?? 0) > 0.8 && (lipGapOpen || aspectOpen);
    const strongOpen = strongGeometric || strongBlendshape;
    const confident = analysis.confidence >= uncertainBelow;

    const status: CheckResult['status'] = !open
      ? 'pass'
      : strongOpen && confident
        ? 'fail'
        : confident
          ? 'warning'
          : 'uncertain';

    const message =
      status === 'pass'
        ? '✅ Рот закрыт'
        : status === 'fail'
          ? '❌ Рот открыт'
          : '⚠ Не удалось надёжно определить положение рта';

    return plainResult({
      id: mouthCheck.id,
      title: mouthCheck.title,
      status,
      severity: ctx.rule.severity,
      value: analysis.lipGap,
      max: maxLipGap,
      unit: 'ratio',
      ...(ctx.rule.official ? { official: ctx.rule.official } : {}),
      message,
      ...(ctx.rule.official?.statement ? { requirement: ctx.rule.official.statement } : {}),
      fixability: 'retake',
      confidence: analysis.confidence,
      details: {
        lipGap: analysis.lipGap,
        mouthAspectRatio: analysis.mouthAspectRatio,
        ...(analysis.jawOpen !== undefined ? { jawOpen: analysis.jawOpen } : {}),
        ...(analysis.teethVisible !== undefined ? { teethVisible: analysis.teethVisible } : {}),
        maxLipGap,
        maxMouthOpenRatio,
        mouthWidthReliable: analysis.mouthWidthReliable,
        blendshapeOpen: analysis.blendshapeOpen,
        uncertainBelowConfidence: uncertainBelow,
        notes: analysis.notes,
        displayValue: status === 'pass' ? 'закрыт' : 'возможно открыт',
      },
    });
  },
};
